import { Injectable, inject } from '@angular/core';
import { RestService } from './rest.service';
import { ErrorReportingService } from './error-reporting.service';
import { AiConsentService } from './ai-consent.service';

export type CheckinMode = 'voice' | 'written';
export type CheckinSection = 'sleep' | 'mood' | 'physical';

export interface DailyCheckinSlider {
  label: string;
  value: string | number;
}

export interface DailyCheckinEntry {
  section: CheckinSection;
  question: string;
  mode: CheckinMode;
  sliders?: DailyCheckinSlider[];
  tags?: string[];
  text?: string;
  transcript?: string;
  audioMimeType?: string;
  durationMs?: number;
  audioToken?: string;
  // Response-only: whether a stored recording exists (raw path stays server-side).
  hasAudio?: boolean;
}

// ---- "My Journal" view model (built client-side from history) ----
export interface JournalVoice {
  checkinId: number;
  entryIndex: number;
  section: CheckinSection;
  transcript: string;
  durationMs?: number;
  hasAudio?: boolean;
  // UI-local playback/edit state.
  editing?: boolean;
  draft?: string;
  audioUrl?: string;
  loadingAudio?: boolean;
}

export interface JournalDay {
  dateKey: string;
  dateLabel: string;
  relativeLabel?: string;
  bucket: 'thisWeek' | 'lastWeek' | 'earlier';
  sleepRange?: string;
  sleepFeeling?: string;
  chips: string[];
  quotes: string[];
  voices: JournalVoice[];
  // Underlying check-in id(s) this day aggregates — used for deletion.
  checkinIds: number[];
  // UI-local: confirm/busy state for the delete action.
  confirmingDelete?: boolean;
  deleting?: boolean;
}

export interface JournalSummary {
  symptom?: string;
  symptomDays?: number;
  totalDays?: number;
  sleepMin?: number;
  sleepMax?: number;
}

export interface JournalModel {
  days: JournalDay[];
  summary: JournalSummary | null;
}

export interface TranscribeResponse {
  transcript: string;
  audioToken?: string;
}

export interface DailyCheckinData {
  id?: number;
  mode: CheckinMode;
  entries: DailyCheckinEntry[];
  aggregatedText?: string;
  created?: string;
}

@Injectable({ providedIn: 'root' })
export class DailyCheckinService {
  private reporter = inject(ErrorReportingService);
  private aiConsent = inject(AiConsentService);

  constructor(private rest: RestService) {}

  async save(data: DailyCheckinData): Promise<DailyCheckinData | null> {
    this.reporter.logEvent('checkin.save.start', {
      mode: data.mode,
      entries: data.entries?.length || 0,
    });
    const resp = await this.rest.request<{
      message: string;
      data: DailyCheckinData;
    }>({
      method: 'POST',
      path: 'daily-checkin/save',
      body: data,
      throwError: true,
    });
    this.reporter.logEvent('checkin.save.ok', { saved: !!resp?.data });
    return resp?.data || null;
  }

  async getToday(): Promise<DailyCheckinData | null> {
    try {
      const resp = await this.rest.request<{
        message: string;
        data: DailyCheckinData | null;
      }>({
        method: 'GET',
        path: 'daily-checkin/today',
        throwError: true,
      });
      return resp?.data || null;
    } catch (error) {
      console.error('DailyCheckinService.getToday failed:', error);
      this.reporter.report(error, 'checkin');
      return null;
    }
  }

  async getHistory(): Promise<DailyCheckinData[]> {
    try {
      const resp = await this.rest.request<{
        message: string;
        data: DailyCheckinData[] | null;
      }>({
        method: 'GET',
        path: 'daily-checkin/history',
        throwError: true,
      });
      return Array.isArray(resp?.data) ? resp!.data : [];
    } catch (error) {
      console.error('DailyCheckinService.getHistory failed:', error);
      this.reporter.report(error, 'checkin');
      return [];
    }
  }

  async getAudioUrl(id: number, entryIndex: number): Promise<string | null> {
    try {
      const resp = await this.rest.request<{ url: string }>({
        method: 'GET',
        path: `daily-checkin/${id}/audio/${entryIndex}`,
        throwError: true,
      });
      return resp?.url || null;
    } catch (error) {
      console.error('DailyCheckinService.getAudioUrl failed:', error);
      this.reporter.report(error, 'checkin');
      return null;
    }
  }

  async deleteCheckin(id: number): Promise<boolean> {
    try {
      await this.rest.request({
        method: 'DELETE',
        path: `daily-checkin/${id}`,
        throwError: true,
      });
      return true;
    } catch (error) {
      console.error('DailyCheckinService.deleteCheckin failed:', error);
      this.reporter.report(error, 'checkin');
      return false;
    }
  }

  async updateTranscript(
    id: number,
    entryIndex: number,
    transcript: string,
  ): Promise<boolean> {
    try {
      await this.rest.request({
        method: 'POST',
        path: `daily-checkin/${id}/entry/${entryIndex}/transcript`,
        body: { transcript },
        throwError: true,
      });
      return true;
    } catch (error) {
      console.error('DailyCheckinService.updateTranscript failed:', error);
      this.reporter.report(error, 'checkin');
      return false;
    }
  }

  /**
   * Build the "My Journal" view model from the raw history list. Pure display +
   * self-report structuring. REGULATORY (§5): the weekly summary numbers come
   * ONLY from structured data (clicked chips + slider values) — never from NLP
   * on free text or transcripts.
   */
  buildJournal(list: DailyCheckinData[], lang: string): JournalModel {
    const locale = lang === 'he' ? 'he-IL' : 'en-US';
    const dayFmt = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

    const now = new Date();
    const todayKey = this.dateKey(now);
    const yKey = this.dateKey(new Date(now.getTime() - 86400000));

    // Group check-ins by calendar day (newest first).
    const groups = new Map<string, DailyCheckinData[]>();
    for (const c of list || []) {
      if (!c?.created) continue;
      const key = this.dateKey(new Date(c.created));
      const arr = groups.get(key) || [];
      arr.push(c);
      groups.set(key, arr);
    }

    const days: JournalDay[] = [];
    const sortedKeys = Array.from(groups.keys()).sort((a, b) =>
      a < b ? 1 : a > b ? -1 : 0,
    );

    for (const key of sortedKeys) {
      const checkins = groups.get(key)!;
      const chips: string[] = [];
      const quotes: string[] = [];
      const voices: JournalVoice[] = [];
      const checkinIds: number[] = [];
      let sleepRange: string | undefined;
      let sleepFeeling: string | undefined;

      for (const c of checkins) {
        if (c.id != null) checkinIds.push(c.id);
        const entries = c.entries || [];
        entries.forEach((e, idx) => {
          // Sleep row: slider values + her wake-feeling tags.
          if (e.section === 'sleep') {
            if (e.sliders && e.sliders.length >= 2 && !sleepRange) {
              sleepRange = `${e.sliders[0].value} – ${e.sliders[1].value}`;
            }
            if (e.tags && e.tags.length) {
              sleepFeeling = sleepFeeling
                ? `${sleepFeeling}, ${e.tags.join(', ')}`
                : e.tags.join(', ');
            }
          } else if (e.tags && e.tags.length) {
            // Symptom chips from other sections (the chips she clicked).
            for (const t of e.tags) if (!chips.includes(t)) chips.push(t);
          }

          const text = (e.text || '').trim();
          if (text) quotes.push(text);

          const hasVoice = e.hasAudio || e.mode === 'voice' || !!e.durationMs;
          const transcript = (e.transcript || '').trim();
          if (hasVoice && (e.hasAudio || transcript)) {
            voices.push({
              checkinId: c.id!,
              entryIndex: idx,
              section: e.section,
              transcript,
              durationMs: e.durationMs,
              hasAudio: e.hasAudio,
            });
          }
        });
      }

      const d = new Date(checkins[0].created!);
      let relativeLabel: string | undefined;
      if (key === todayKey) relativeLabel = '__today__';
      else if (key === yKey) relativeLabel = '__yesterday__';

      const diffDays = Math.floor(
        (this.startOfDay(now).getTime() - this.startOfDay(d).getTime()) /
          86400000,
      );
      const bucket: JournalDay['bucket'] =
        diffDays <= 6 ? 'thisWeek' : diffDays <= 13 ? 'lastWeek' : 'earlier';

      days.push({
        dateKey: key,
        dateLabel: dayFmt.format(d),
        relativeLabel,
        bucket,
        sleepRange,
        sleepFeeling,
        chips,
        quotes,
        voices,
        checkinIds,
      });
    }

    return { days, summary: this.buildWeekSummary(list) };
  }

  // §5: numbers from structured data ONLY (chips + sleep sliders), last 7 days.
  private buildWeekSummary(list: DailyCheckinData[]): JournalSummary | null {
    const now = new Date();
    const cutoff = this.startOfDay(now).getTime() - 6 * 86400000;

    const symptomDayKeys = new Map<string, Set<string>>();
    const sleepHours: number[] = [];
    const daysWithData = new Set<string>();

    for (const c of list || []) {
      if (!c?.created) continue;
      const dt = new Date(c.created);
      if (this.startOfDay(dt).getTime() < cutoff) continue;
      const dayKey = this.dateKey(dt);

      for (const e of c.entries || []) {
        if (e.tags && e.tags.length) {
          daysWithData.add(dayKey);
          for (const t of e.tags) {
            const set = symptomDayKeys.get(t) || new Set<string>();
            set.add(dayKey);
            symptomDayKeys.set(t, set);
          }
        }
        if (e.section === 'sleep' && e.sliders && e.sliders.length >= 2) {
          const fall = this.parseClockHour(String(e.sliders[0].value));
          const wake = this.parseClockHour(String(e.sliders[1].value));
          if (fall != null && wake != null) {
            daysWithData.add(dayKey);
            sleepHours.push(((wake - fall + 24) % 24) || 24);
          }
        }
      }
    }

    // Top clicked symptom appearing on >= 2 distinct days.
    let symptom: string | undefined;
    let symptomDays = 0;
    for (const [tag, set] of symptomDayKeys) {
      if (set.size >= 2 && set.size > symptomDays) {
        symptom = tag;
        symptomDays = set.size;
      }
    }

    const hasSymptom = !!symptom;
    const hasSleep = sleepHours.length >= 2;
    if (!hasSymptom && !hasSleep) return null;

    return {
      symptom: hasSymptom ? symptom : undefined,
      symptomDays: hasSymptom ? symptomDays : undefined,
      totalDays: hasSymptom ? daysWithData.size : undefined,
      sleepMin: hasSleep ? Math.min(...sleepHours) : undefined,
      sleepMax: hasSleep ? Math.max(...sleepHours) : undefined,
    };
  }

  // Parses the slider display value "11:00 PM" / "7:00 AM" back to a 0-23 hour.
  private parseClockHour(s: string): number | null {
    const m = /(\d{1,2}):\d{2}\s*(AM|PM)/i.exec(s || '');
    if (!m) return null;
    let h = Number(m[1]) % 12;
    if (/PM/i.test(m[2])) h += 12;
    return h;
  }

  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private dateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  async transcribe(
    audioBase64: string,
    mimeType: string,
    clientTranscript?: string,
  ): Promise<TranscribeResponse> {
    // App Review 5.1.2(i): nothing goes to Gemini until she agreed once; without
    // it we keep the on-device browser transcript only.
    if (!(await this.aiConsent.ensure('voice'))) {
      return { transcript: clientTranscript || '' };
    }
    try {
      const lang =
        document.documentElement.lang || navigator.language || 'en';
      // Log shapes only — never the audio payload or the transcript text.
      this.reporter.logEvent('checkin.transcribe.start', {
        mimeType,
        bytes: audioBase64?.length || 0,
        hasClientTranscript: !!clientTranscript,
        lang,
      });
      const resp = await this.rest.request<TranscribeResponse>({
        method: 'POST',
        path: 'daily-checkin/transcribe',
        body: { audioBase64, mimeType, clientTranscript, lang },
        throwError: true,
      });
      this.reporter.logEvent('checkin.transcribe.ok', {
        hasTranscript: !!resp?.transcript,
        hasToken: !!resp?.audioToken,
      });
      return {
        transcript: resp?.transcript || '',
        audioToken: resp?.audioToken,
      };
    } catch (error) {
      console.error('DailyCheckinService.transcribe failed:', error);
      this.reporter.report(error, 'checkin');
      return { transcript: '' };
    }
  }
}
